import React, { useRef, useState, useEffect } from 'react';
import { connect } from 'react-redux';
import Paper from '@material-ui/core/Paper';
import Grid from '@material-ui/core/Grid';
import Button from '@material-ui/core/Button';
import IconButton from '@material-ui/core/IconButton';
import Typography from '@material-ui/core/Typography';
import Hidden from '@material-ui/core/Hidden';
import Drawer from '@material-ui/core/Drawer';
import CloseOutlinedIcon from '@material-ui/icons/CloseOutlined';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import BorderStyleIcon from '@material-ui/icons/BorderStyle';
import WallpaperIcon from '@material-ui/icons/Wallpaper';
import queryString from 'query-string';

import useARModel from './ar/useARModel';
import { isAndroid, launchSceneViewer } from './ar/sceneViewer';
import { styles } from './ar/ARViewer.styles';
import { fetchViewSettingsRequest, renderOverlay } from './viewSettingsAction';
import BlockUI from '../Common/plugins/Block_UI';
import OverlayContent from './OverlayContent';
import Frames from './FramesComponent';

// Fixed for the life of the session — the UA does not change under us.
const ANDROID = isAndroid();

const closeModal = () => window.parent.postMessage('closeModal', '*');

const backTo2D = () => {
    const params = new URLSearchParams(window.location.search);
    params.delete('mode');
    window.location.search = params.toString();
};

const ARViewer = ({ artwork, loading, displayOverlay, frameType, fetchViewSettingsRequest, renderOverlay }) => {
    useEffect(() => {
        const { clientId, artworkId } = queryString.parse(window.location.search);
        if (clientId && artworkId) fetchViewSettingsRequest({ clientId, artworkId });
    }, [fetchViewSettingsRequest]);

    const mvRef = useRef(null);
    const { objectUrl, status, ready, error } = useARModel(frameType);
    const [arSupported, setArSupported] = useState(null); // null=unknown, true/false
    const [framesOpen, setFramesOpen] = useState(false);

    // Once the GLB is loaded into model-viewer, check if AR can activate
    useEffect(() => {
        const mv = mvRef.current;
        if (!mv || !objectUrl) return;

        const onLoad = () => setArSupported(mv.canActivateAR);
        mv.addEventListener('load', onLoad, { once: true });
        return () => mv.removeEventListener('load', onLoad);
    }, [objectUrl]);

    const onARClick = () => {
        const mv = mvRef.current;
        // Android: hand off to Scene Viewer directly (see ./ar/sceneViewer.js).
        // This launches even when model-viewer reports canActivateAR === false,
        // which happens on some Android browsers despite full ARCore support and
        // is what used to leave the button dead. Needs only the hosted GLB URL.
        if (ANDROID && objectUrl) {
            launchSceneViewer(objectUrl, window.location.href);
            return;
        }
        // iOS Quick Look / WebXR — unchanged native handoff via model-viewer.
        if (mv?.canActivateAR) mv.activateAR();
    };

    return (
        <BlockUI dataLoading={loading}>
        <div style={styles.root}>
            {displayOverlay && <OverlayContent />}

            <Paper style={styles.header}>
                <Grid container justify="space-between" alignItems="center">
                    <Grid item>
                        <b>View in Your Room</b>
                    </Grid>
                    <Grid item>
                        <Grid container spacing={1} alignItems="center">
                            <Grid item>
                                <IconButton size="small" color="primary" onClick={backTo2D}>
                                    <WallpaperIcon />
                                    <Hidden xsDown> Wall view</Hidden>
                                </IconButton>
                            </Grid>
                            <Grid item>
                                <IconButton size="small" color="primary" onClick={() => renderOverlay(true)}>
                                    <InfoOutlinedIcon />
                                </IconButton>
                            </Grid>
                            <Grid item>
                                <IconButton size="small" color="primary" onClick={() => setFramesOpen(true)}>
                                    <BorderStyleIcon />
                                    <Hidden xsDown>Frames</Hidden>
                                </IconButton>
                                <Drawer anchor="right" open={framesOpen} onClose={() => setFramesOpen(false)}>
                                    <Frames />
                                </Drawer>
                            </Grid>
                            <Grid item>
                                <IconButton size="small" color="primary" onClick={closeModal}>
                                    <CloseOutlinedIcon />
                                </IconButton>
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>
            </Paper>

            <div style={styles.content}>
                <model-viewer
                    ref={mvRef}
                    src={objectUrl}
                    alt={artwork?.title ?? ''}
                    camera-controls
                    auto-rotate
                    auto-rotate-delay="2000"
                    shadow-intensity="1"
                    ar
                    ar-modes="webxr scene-viewer quick-look"
                    ar-scale="fixed"
                    style={styles.viewer}
                />

                {status && (
                    <Typography variant="caption" style={styles.status}>
                        {status}
                    </Typography>
                )}

                {error && (
                    <div style={styles.errorBox}>
                        <Typography variant="body2"><b>Unable to load AR view</b></Typography>
                        <Typography variant="body2" style={{ marginTop: 4 }}>{error.message}</Typography>
                    </div>
                )}

                {arSupported === false && !error && !ANDROID && (
                    <div style={styles.warningBox}>
                        <Typography variant="body2">
                            <b>AR not supported on this device</b>
                        </Typography>
                        <Typography variant="body2" style={{ marginTop: 4 }}>
                            Try opening this page on an iPhone, iPad, or Android device.
                        </Typography>
                    </div>
                )}

                {!error && (
                    <Button
                        variant="contained"
                        color="primary"
                        disabled={!ready || (arSupported === false && !ANDROID)}
                        onClick={onARClick}
                        style={styles.btn}
                    >
                        View in Your Room
                    </Button>
                )}
            </div>
        </div>
        </BlockUI>
    );
};

const mapStateToProps = ({ viewSettings }) => ({
    artwork:        viewSettings.list?.image ? viewSettings.list : null,
    loading:        viewSettings.loading,
    displayOverlay: viewSettings.displayOverlay,
    frameType:      viewSettings.frame,
});

export default connect(mapStateToProps, { fetchViewSettingsRequest, renderOverlay })(ARViewer);
